import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './OwnerAssignment.css';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
}

interface Building {
  id: number;
  name: string;
  address: string;
}

interface Floor {
  id: number;
  floor_number: number;
  building_id: number;
}

interface Flat {
  id: number;
  flat_number: string;
  flat_type: string;
  bedrooms: number;
  bathrooms: number;
  area_sqft?: number;
  status: 'available' | 'occupied';
  owner_id?: number;
  floor_id: number;
}

interface AssignmentFormData {
  user_id: number | null;
  building_id: number | null;
  floor_id: number | null;
  flat_id: number | null;
  ownership_type: 'owner' | 'co_owner' | 'legal_heir';
  ownership_percentage: number;
  is_primary_owner: boolean;
}

const OwnerAssignment: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [flats, setFlats] = useState<Flat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  const [assignmentForm, setAssignmentForm] = useState<AssignmentFormData>({
    user_id: null,
    building_id: null,
    floor_id: null,
    flat_id: null,
    ownership_type: 'owner',
    ownership_percentage: 100,
    is_primary_owner: true,
  });

  const fetchUsers = async (params?: { building_id?: number | null; floor_id?: number | null; flat_id?: number | null }) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('per_page', '100');
      if (params?.building_id) searchParams.append('building_id', String(params.building_id));
      if (params?.floor_id) searchParams.append('floor_id', String(params.floor_id));
      if (params?.flat_id) searchParams.append('flat_id', String(params.flat_id));

      const response = await api.get(`/users?${searchParams.toString()}`);
      if (response.data.success) {
        setUsers(response.data.data.data || response.data.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchBuildings = async () => {
    try {
      const response = await api.get('/buildings');
      if (response.data.success) {
        setBuildings(response.data.data.data || response.data.data);
      }
    } catch (err) {
      console.error('Error fetching buildings:', err);
    }
  };

  const fetchFloors = async (buildingId: number) => {
    try {
      console.log('Fetching floors for building:', buildingId);
      const response = await api.get(`/buildings/${buildingId}/floors`);
      console.log('Floors API response:', response.data);
      if (response.data.success) {
        setFloors(response.data.data);
        console.log('Floors set:', response.data.data);
      }
    } catch (err) {
      console.error('Error fetching floors:', err);
    }
  };

  const fetchFlats = async (floorId: number) => {
    try {
      const response = await api.get(`/floors/${floorId}/flats`);
      if (response.data.success) {
        setFlats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching flats:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchBuildings();
  }, []);

  // Set users when loaded
  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  // Debug floors state
  useEffect(() => {
    console.log('Floors state updated:', floors);
  }, [floors]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await api.post('/owners', assignmentForm);
      if (response.data.success) {
        setSuccess('Owner assigned successfully!');
        resetForm();
        // Refresh flats to show updated status
        if (assignmentForm.floor_id) {
          fetchFlats(assignmentForm.floor_id);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign owner');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'building_id') {
      const buildingId = parseInt(value) || null;
      console.log('Building selected:', buildingId);
      setAssignmentForm(prev => ({
        ...prev,
        building_id: buildingId,
        floor_id: null, // Reset floor selection when building changes
        flat_id: null // Reset flat selection when building changes
      }));
      
      if (buildingId) {
        console.log('Calling fetchFloors with buildingId:', buildingId);
        fetchFloors(buildingId);
      } else {
        setFloors([]);
        setFlats([]);
      }
    } else if (name === 'floor_id') {
      const floorId = parseInt(value) || null;
      setAssignmentForm(prev => ({
        ...prev,
        floor_id: floorId,
        flat_id: null // Reset flat selection when floor changes
      }));
      
      if (floorId) {
        fetchFlats(floorId);
      } else {
        setFlats([]);
      }
    } else {
      setAssignmentForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
                type === 'number' ? parseFloat(value) || 0 : value
      }));
    }
  };

  // When flat selection changes, refetch users filtered by current selection
  useEffect(() => {
    if (assignmentForm.flat_id) {
      fetchUsers({
        building_id: assignmentForm.building_id,
        floor_id: assignmentForm.floor_id,
        flat_id: assignmentForm.flat_id
      });
    } else if (assignmentForm.floor_id || assignmentForm.building_id) {
      fetchUsers({
        building_id: assignmentForm.building_id,
        floor_id: assignmentForm.floor_id
      });
    } else {
      fetchUsers();
    }
  }, [assignmentForm.building_id, assignmentForm.floor_id, assignmentForm.flat_id]);

  const resetForm = () => {
    setAssignmentForm({
      user_id: null,
      building_id: null,
      floor_id: null,
      flat_id: null,
      ownership_type: 'owner',
      ownership_percentage: 100,
      is_primary_owner: true,
    });
    setFloors([]);
    setFlats([]);
  };

  const getFlatStatusColor = (status: string) => {
    return status === 'available' ? 'success' : 'danger';
  };

  return (
    <div className="owner-assignment">
      <div className="assignment-header">
        <div className="header-content">
          <h1>Assign Flat Owner</h1>
          <p>Quickly assign users as owners of specific flats.</p>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-times-circle error-icon"></i>
          <span className="error-text">{error}</span>
          <button className="error-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <i className="fas fa-check-circle success-icon"></i>
          <span className="success-text">{success}</span>
          <button className="success-close" onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <div className="assignment-content">
        <div className="assignment-form">
          <h2>Owner Assignment</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="user_id">Select User *</label>
                <select
                  id="user_id"
                  name="user_id"
                  value={assignmentForm.user_id || ''}
                  onChange={handleFormChange}
                  required
                  className="form-control"
                >
                  <option value="">Select a user...</option>
                  {filteredUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} - {user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="building_id">Select Building *</label>
                <select
                  id="building_id"
                  name="building_id"
                  value={assignmentForm.building_id || ''}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Choose a building...</option>
                  {buildings.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.name} - {building.address}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="floor_id">Select Floor *</label>
                <select
                  id="floor_id"
                  name="floor_id"
                  value={assignmentForm.floor_id || ''}
                  onChange={handleFormChange}
                  required
                  disabled={!assignmentForm.building_id}
                >
                  <option value="">Choose a floor...</option>
                  {floors.map(floor => (
                    <option key={floor.id} value={floor.id}>
                      Floor {floor.floor_number}
                    </option>
                  ))}
                </select>
                {assignmentForm.building_id && floors.length === 0 && (
                  <small className="text-muted">Loading floors...</small>
                )}
                {assignmentForm.building_id && floors.length > 0 && (
                  <small className="text-success">Found {floors.length} floors</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="flat_id">Select Flat *</label>
                <select
                  id="flat_id"
                  name="flat_id"
                  value={assignmentForm.flat_id || ''}
                  onChange={handleFormChange}
                  required
                  disabled={!assignmentForm.floor_id}
                >
                  <option value="">Choose a flat...</option>
                  {flats.map(flat => (
                    <option key={flat.id} value={flat.id} disabled={flat.status === 'occupied'}>
                      {flat.flat_number} ({flat.flat_type}) - {flat.status === 'available' ? 'Available' : 'Occupied'}
                    </option>
                  ))}
                </select>
                {assignmentForm.floor_id && flats.length === 0 && (
                  <small className="text-muted">Loading flats...</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="ownership_type">Ownership Type *</label>
                <select
                  id="ownership_type"
                  name="ownership_type"
                  value={assignmentForm.ownership_type}
                  onChange={handleFormChange}
                  required
                >
                  <option value="owner">Owner</option>
                  <option value="co_owner">Co-Owner</option>
                  <option value="legal_heir">Legal Heir</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="ownership_percentage">Ownership Percentage *</label>
                <input
                  type="number"
                  id="ownership_percentage"
                  name="ownership_percentage"
                  value={assignmentForm.ownership_percentage}
                  onChange={handleFormChange}
                  min="0"
                  max="100"
                  step="0.01"
                  required
                />
              </div>


              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_primary_owner"
                    checked={assignmentForm.is_primary_owner}
                    onChange={handleFormChange}
                  />
                  Primary Owner
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Reset Form
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Assigning...' : 'Assign Owner'}
              </button>
            </div>
          </form>
        </div>

        {assignmentForm.building_id && flats.length > 0 && (
          <div className="flats-preview">
            <h3>Available Flats in {buildings.find(b => b.id === assignmentForm.building_id)?.name}</h3>
            <div className="flats-grid">
              {flats.map(flat => (
                <div key={flat.id} className={`flat-card ${flat.status}`}>
                  <div className="flat-header">
                    <h4>{flat.flat_number}</h4>
                    <span className={`status-badge ${getFlatStatusColor(flat.status)}`}>
                      {flat.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flat-details">
                    <p><strong>Type:</strong> {flat.flat_type}</p>
                    <p><strong>Bedrooms:</strong> {flat.bedrooms}</p>
                    <p><strong>Bathrooms:</strong> {flat.bathrooms}</p>
                    {flat.area_sqft && <p><strong>Area:</strong> {flat.area_sqft} sq ft</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerAssignment;
